import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { useUnit } from 'effector-react';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { devicesApi } from '../../api/devices';
import { heatApi } from '../../api/heat';
import { createDeviceFx } from '../../store/devices';
import { $apartmentsByCorp, createApartmentFx, fetchApartmentsByCorpFx } from '../../store/apartments';

const SYSTEM_TYPES = ['ХВС', 'ГВС', 'Отопление', 'Электросчетчик', 'УРТ'];
const COLLECTION_MODES = [
  { value: 'file_import', label: 'Импорт из файла' },
  { value: 'automatic', label: 'Автоматический сбор' },
];
const filterModelOptions = createFilterOptions();

const initialForm = () => ({
  meter_type: '',
  model: '',
  factory_no: '',
  seal_date: '',
  verification_date: '',
  deveui: '',
  mqttTopic: '',
  reading_collection_mode: 'automatic',
  description: '',
  aptSelected: null,
  newAptMode: false,
  newAptNumber: '',
  newAptSuffix: '',
  newAptArea: '',
  newAptFloor: '',
  newAptSection: '',
  newAptOwner: '',
  heat_coefficient: '',
  heat_installation_date: '',
  heat_seal_status: '',
  heat_operational_status: '',
  heat_notes: '',
  heat_is_direct: false,
  radiator_model_name: '',
  radiator_manufacturer: '',
  radiator_placement: '',
  radiator_room_label: '',
  radiator_section_count: '',
  radiator_power_watts: '',
  radiator_height_mm: '',
  radiator_width_mm: '',
  radiator_depth_mm: '',
  radiator_is_corner: false,
  radiator_notes: '',
});

const normalizeText = (value) => (value ?? '').toString();

const parseNumber = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseInteger = (value) => {
  const parsed = parseNumber(value);
  if (parsed === null) return null;
  return Number.isInteger(parsed) ? parsed : Math.trunc(parsed);
};

const formatApartmentLabel = (apartment) =>
  [
    apartment.number,
    apartment.suffix || '',
    apartment.area ? `${apartment.area} м²` : '',
    apartment.section_number ? `секц. ${apartment.section_number}` : '',
    apartment.floor_number ? `эт. ${apartment.floor_number}` : '',
  ]
    .filter(Boolean)
    .join(' • ');

const formatRadiatorLabel = (item) => {
  if (!item) return '';
  return [
    item.name,
    item.model_code || null,
    item.power_watts != null ? `${item.power_watts} Вт` : null,
  ]
    .filter(Boolean)
    .join(' • ');
};

const FieldLabel = ({ label, title }) => (
  <Stack component="span" direction="row" spacing={0.5} alignItems="center">
    <span>{label}</span>
    <Tooltip title={title} arrow>
      <HelpOutlineRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
    </Tooltip>
  </Stack>
);

export default function AddDeviceDialog({
  open,
  onClose,
  corpsId,
  buildingId,
  fixedApartment = null,
  onSuccess,
  houseMode = false,
}) {
  const pendingCreateDevice = useUnit(createDeviceFx.pending);
  const pendingCreateApartment = useUnit(createApartmentFx.pending);
  const apartmentsMap = useUnit($apartmentsByCorp);

  const apartments = useMemo(() => apartmentsMap[String(corpsId)] || [], [apartmentsMap, corpsId]);

  const [error, setError] = useState('');
  const [radiatorQuery, setRadiatorQuery] = useState('');
  const [radiatorCatalogItems, setRadiatorCatalogItems] = useState([]);
  const [radiatorCatalogLoading, setRadiatorCatalogLoading] = useState(false);
  const [selectedRadiator, setSelectedRadiator] = useState(null);
  const [manualHeatMode, setManualHeatMode] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [modelOptionsLoading, setModelOptionsLoading] = useState(false);
  const modelOptionsCache = useRef(new Map());
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (open && corpsId && !fixedApartment) {
      fetchApartmentsByCorpFx(String(corpsId));
    }
  }, [open, corpsId, fixedApartment]);

  useEffect(() => {
    if (open) {
      setError('');
      setForm({
        ...initialForm(),
        aptSelected: fixedApartment
          ? {
              id: fixedApartment.id,
              number: fixedApartment.number,
              suffix: fixedApartment.suffix || '',
              area: fixedApartment.area || '',
              floor_number: fixedApartment.floor_number || '',
              section_number: fixedApartment.section_number || '',
              owner_name: fixedApartment.owner_name || '',
            }
          : null,
      });
      setRadiatorQuery('');
      setRadiatorCatalogItems([]);
      setRadiatorCatalogLoading(false);
      setSelectedRadiator(null);
      setManualHeatMode(false);
    }
  }, [open, fixedApartment]);

  const isUrt = form.meter_type === 'УРТ';

  useEffect(() => {
    if (!open || !buildingId || !form.meter_type) {
      setModelOptions([]);
      setModelOptionsLoading(false);
      return undefined;
    }

    const cacheKey = `${buildingId}:${form.meter_type}`;
    const cached = modelOptionsCache.current.get(cacheKey);
    if (cached) {
      setModelOptions(cached);
      setModelOptionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setModelOptionsLoading(true);
    devicesApi
      .getModelSuggestions({
        building_id: String(buildingId),
        system_type: form.meter_type,
      })
      .then((items) => {
        if (cancelled) return;
        const normalizedItems = Array.from(
          new Map(
            (items || [])
              .map((item) => String(item || '').trim())
              .filter(Boolean)
              .map((item) => [item.toLocaleLowerCase('ru-RU'), item])
          ).values()
        ).sort(new Intl.Collator('ru-RU', { numeric: true, sensitivity: 'base' }).compare);
        modelOptionsCache.current.set(cacheKey, normalizedItems);
        setModelOptions(normalizedItems);
      })
      .catch(() => {
        if (!cancelled) setModelOptions([]);
      })
      .finally(() => {
        if (!cancelled) setModelOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [buildingId, form.meter_type, open]);

  const loadRadiatorCatalog = useCallback(async (query = '') => {
    setRadiatorCatalogLoading(true);
    try {
      const data = await heatApi.listCatalog(query.trim());
      setRadiatorCatalogItems(data.items || []);
    } catch {
      setRadiatorCatalogItems([]);
    } finally {
      setRadiatorCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !isUrt) return undefined;

    const timer = setTimeout(() => {
      loadRadiatorCatalog(radiatorQuery);
    }, 250);

    return () => clearTimeout(timer);
  }, [open, isUrt, radiatorQuery, loadRadiatorCatalog]);

  const canSubmit =
    !!form.meter_type &&
    (houseMode || form.aptSelected || form.newAptMode) &&
    !!form.factory_no.trim() &&
    (!isUrt || selectedRadiator || manualHeatMode);

  const aptOptions = useMemo(
    () =>
      apartments.map((a) => ({
        id: a.id,
        number: a.number,
        suffix: a.suffix || '',
        area: a.area || '',
        floor_number: a.floor_number || '',
        section_number: a.section_number || '',
        owner_name: a.owner_name || '',
        label: formatApartmentLabel(a),
      })),
    [apartments]
  );

  const clearHeatSelection = () => {
    setSelectedRadiator(null);
    setManualHeatMode(false);
    setForm((prev) => ({
      ...prev,
      heat_coefficient: '',
      heat_installation_date: '',
      heat_seal_status: '',
      heat_operational_status: '',
      heat_notes: '',
      heat_is_direct: false,
      radiator_model_name: '',
      radiator_manufacturer: '',
      radiator_placement: '',
      radiator_room_label: '',
      radiator_section_count: '',
      radiator_power_watts: '',
      radiator_height_mm: '',
      radiator_width_mm: '',
      radiator_depth_mm: '',
      radiator_is_corner: false,
      radiator_notes: '',
    }));
  };

  const handleMeterTypeChange = (value) => {
    setForm((prev) => ({
      ...prev,
      meter_type: value,
    }));

    if (value !== 'УРТ') {
      clearHeatSelection();
    }
  };

  const handleRadiatorSelect = (_, value) => {
    setSelectedRadiator(value || null);
    setManualHeatMode(false);
    setForm((prev) => ({
      ...prev,
      heat_coefficient:
        value?.heat_coefficient !== null && value?.heat_coefficient !== undefined
          ? String(value.heat_coefficient)
          : '',
      radiator_model_name: value?.name || '',
      radiator_manufacturer: value?.manufacturer || '',
      radiator_placement: value?.placement || '',
      radiator_section_count: value?.section_count || '',
      radiator_power_watts:
        value?.power_watts !== null && value?.power_watts !== undefined ? String(value.power_watts) : '',
      radiator_height_mm:
        value?.height_mm !== null && value?.height_mm !== undefined ? String(value.height_mm) : '',
      radiator_width_mm:
        value?.width_mm !== null && value?.width_mm !== undefined ? String(value.width_mm) : '',
      radiator_depth_mm:
        value?.depth_mm !== null && value?.depth_mm !== undefined ? String(value.depth_mm) : '',
      radiator_is_corner: !!value?.is_corner,
      radiator_notes: value?.notes || '',
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || pendingCreateDevice || pendingCreateApartment) return;
    setError('');

    try {
      let apt = form.aptSelected;

      if (houseMode && !apt) {
        const result = await fetchApartmentsByCorpFx(String(corpsId));
        apt = result?.list?.[0] || apartments[0] || null;
        if (!apt) {
          apt = await createApartmentFx({
            corp_id: String(corpsId),
            number: '1',
          });
        }
      }

      if (form.newAptMode) {
        if (!form.newAptNumber.trim()) {
          setError('Укажите номер квартиры');
          return;
        }
        const created = await createApartmentFx({
          corp_id: String(corpsId),
          number: form.newAptNumber.trim(),
          suffix: form.newAptSuffix.trim() || undefined,
          area: form.newAptArea.trim() || undefined,
          floor_number: form.newAptFloor.trim() || undefined,
          section_number: form.newAptSection.trim() || undefined,
          owner_name: form.newAptOwner.trim() || undefined,
        });
        apt = created;
      }

      if (isUrt && !manualHeatMode && !selectedRadiator) {
        setError('Выберите радиатор из каталога или включите ручной ввод');
        return;
      }

      if (isUrt && manualHeatMode) {
        const kValue = parseNumber(form.heat_coefficient);
        if (kValue === null) {
          setError('Укажите расчётный коэффициент радиатора');
          return;
        }
      }

      await createDeviceFx({
        corps_id: String(corpsId),
        building_id: String(buildingId || ''),
        apartment_id: String(apt.id),
        system_type: form.meter_type,
        manufacturer: form.model.trim() || undefined,
        serial_number: form.factory_no.trim() || undefined,
        seal_date: form.seal_date || undefined,
        verification_date: form.verification_date || undefined,
        dev_eui: form.deveui.trim() || undefined,
        mqtt_topic: form.mqttTopic.trim() || undefined,
        reading_collection_mode: form.reading_collection_mode,
        room_number: apt?.number ? Number(apt.number) : undefined,
        suffix: apt?.suffix || undefined,
        description: form.description.trim() || undefined,
        heat_coefficient: isUrt ? parseNumber(form.heat_coefficient) ?? undefined : undefined,
        heat_installation_date: isUrt ? form.heat_installation_date || undefined : undefined,
        heat_seal_status: isUrt ? form.heat_seal_status.trim() || undefined : undefined,
        heat_operational_status: isUrt ? form.heat_operational_status.trim() || undefined : undefined,
        heat_notes: isUrt ? form.heat_notes.trim() || undefined : undefined,
        heat_is_direct: isUrt ? !!form.heat_is_direct : undefined,
        radiator_catalog_id: isUrt && selectedRadiator ? selectedRadiator.id : undefined,
        radiator_model_name: isUrt ? form.radiator_model_name.trim() || undefined : undefined,
        radiator_manufacturer: isUrt ? form.radiator_manufacturer.trim() || undefined : undefined,
        radiator_placement: isUrt ? form.radiator_placement.trim() || undefined : undefined,
        radiator_room_label: isUrt ? form.radiator_room_label.trim() || undefined : undefined,
        radiator_section_count: isUrt ? parseInteger(form.radiator_section_count) ?? undefined : undefined,
        radiator_power_watts: isUrt ? parseNumber(form.radiator_power_watts) ?? undefined : undefined,
        radiator_height_mm: isUrt ? parseInteger(form.radiator_height_mm) ?? undefined : undefined,
        radiator_width_mm: isUrt ? parseInteger(form.radiator_width_mm) ?? undefined : undefined,
        radiator_depth_mm: isUrt ? parseInteger(form.radiator_depth_mm) ?? undefined : undefined,
        radiator_is_corner: isUrt ? !!form.radiator_is_corner : undefined,
        radiator_notes: isUrt ? form.radiator_notes.trim() || undefined : undefined,
      });

      onSuccess?.();
      onClose?.();
    } catch {
      setError('Не удалось создать устройство. Проверьте данные и попробуйте снова.');
    }
  };

  const renderHeatSummary = () => {
    if (!selectedRadiator || manualHeatMode) {
      return null;
    }

    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {selectedRadiator.name}
            </Typography>
            {selectedRadiator.power_watts != null && (
              <Chip size="small" label={`${selectedRadiator.power_watts} Вт`} />
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`K: ${selectedRadiator.heat_coefficient ?? '—'}`} />
            {selectedRadiator.model_code && <Chip size="small" variant="outlined" label={selectedRadiator.model_code} />}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={pendingCreateDevice ? undefined : onClose} fullWidth maxWidth="md">
      <form onSubmit={submit}>
        <DialogTitle>{houseMode ? 'Добавить прибор' : 'Добавить устройство'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              select
              label="Тип счётчика"
              value={form.meter_type}
              onChange={(e) => handleMeterTypeChange(e.target.value)}
              required
            >
              {SYSTEM_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type === 'УРТ' ? 'УРТ (прибор отопления)' : type}
                </MenuItem>
              ))}
            </TextField>

            {houseMode ? null : fixedApartment ? (
              <Paper
                variant="outlined"
                sx={{ px: 1.5, py: 1.25, borderRadius: 2.5, bgcolor: '#F8FBFF' }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Квартира
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 800 }}>
                  № {normalizeText(fixedApartment.number)}{normalizeText(fixedApartment.suffix)}
                </Typography>
              </Paper>
            ) : (
            <Stack spacing={1.25}>
              <Autocomplete
                disabled={form.newAptMode}
                options={aptOptions}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  form.aptSelected
                    ? {
                        ...form.aptSelected,
                        label: formatApartmentLabel(form.aptSelected),
                      }
                    : null
                }
                onChange={(_, val) =>
                  setForm((prev) => ({
                    ...prev,
                    aptSelected: val
                      ? {
                          id: val.id,
                          number: val.number,
                          suffix: val.suffix,
                          area: val.area,
                          floor_number: val.floor_number,
                          section_number: val.section_number,
                          owner_name: val.owner_name,
                        }
                      : null,
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Квартира"
                    placeholder="Выберите квартиру"
                  />
                )}
                noOptionsText="Квартиры не найдены"
              />

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button
                  variant={form.newAptMode ? 'contained' : 'outlined'}
                  onClick={() => setForm((prev) => ({ ...prev, newAptMode: !prev.newAptMode }))}
                >
                  {form.newAptMode ? 'Отменить создание квартиры' : 'Создать новую квартиру'}
                </Button>

                {form.aptSelected && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ ml: 'auto' }}>
                    <Chip size="small" variant="outlined" label={`№ ${normalizeText(form.aptSelected.number)}`} />
                    {form.aptSelected.suffix && (
                      <Chip size="small" variant="outlined" label={`Суффикс ${normalizeText(form.aptSelected.suffix)}`} />
                    )}
                  </Stack>
                )}
              </Stack>

              {form.newAptMode && (
                <Stack
                  spacing={1.2}
                  sx={{
                    p: 1.25,
                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Номер квартиры"
                      value={form.newAptNumber}
                      onChange={(e) => setForm((prev) => ({ ...prev, newAptNumber: e.target.value }))}
                      required
                    />
                    <TextField
                      label="Суффикс"
                      value={form.newAptSuffix}
                      onChange={(e) => setForm((prev) => ({ ...prev, newAptSuffix: e.target.value }))}
                    />
                    <TextField
                      label="Площадь, м²"
                      value={form.newAptArea}
                      onChange={(e) => setForm((prev) => ({ ...prev, newAptArea: e.target.value }))}
                      placeholder="например, 72.5"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Секция"
                      value={form.newAptSection}
                      onChange={(e) => setForm((prev) => ({ ...prev, newAptSection: e.target.value }))}
                    />
                    <TextField
                      label="Этаж"
                      value={form.newAptFloor}
                      onChange={(e) => setForm((prev) => ({ ...prev, newAptFloor: e.target.value }))}
                    />
                    <TextField
                      label="Владелец"
                      value={form.newAptOwner}
                      onChange={(e) => setForm((prev) => ({ ...prev, newAptOwner: e.target.value }))}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete
                freeSolo
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                fullWidth
                options={modelOptions}
                loading={modelOptionsLoading}
                value={form.model}
                inputValue={form.model}
                getOptionLabel={(option) => (
                  typeof option === 'string' ? option : option?.inputValue || ''
                )}
                isOptionEqualToValue={(option, value) => {
                  const optionValue = typeof option === 'string' ? option : option?.inputValue;
                  const selectedValue = typeof value === 'string' ? value : value?.inputValue;
                  return optionValue === selectedValue;
                }}
                filterOptions={(options, params) => {
                  const filtered = filterModelOptions(options, params);
                  const inputValue = params.inputValue.trim();
                  const exists = options.some(
                    (option) => option.toLocaleLowerCase('ru-RU') === inputValue.toLocaleLowerCase('ru-RU')
                  );
                  if (inputValue && !exists) {
                    filtered.push({ inputValue, label: `Добавить «${inputValue}»` });
                  }
                  return filtered;
                }}
                onChange={(_, value) => {
                  const nextValue = typeof value === 'string' ? value : value?.inputValue || '';
                  setForm((prev) => ({ ...prev, model: nextValue }));
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'reset') return;
                  setForm((prev) => ({ ...prev, model: value }));
                }}
                renderOption={(props, option) => (
                  <li {...props} key={typeof option === 'string' ? option : `new-${option.inputValue}`}>
                    {typeof option === 'string' ? option : option.label}
                  </li>
                )}
                loadingText="Загрузка моделей..."
                noOptionsText="Модели не найдены"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Модель счётчика"
                    placeholder="Выберите или введите"
                    helperText={modelOptions.length ? 'Можно выбрать из ЖК или ввести новую' : 'Введите новую модель'}
                  />
                )}
              />
              <TextField
                label="Заводской номер"
                value={form.factory_no}
                onChange={(e) => setForm((prev) => ({ ...prev, factory_no: e.target.value }))}
                fullWidth
                required
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Дата пломбировки"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.seal_date}
                onChange={(e) => setForm((prev) => ({ ...prev, seal_date: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Дата поверки"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.verification_date}
                onChange={(e) => setForm((prev) => ({ ...prev, verification_date: e.target.value }))}
                fullWidth
              />
            </Stack>

            <TextField
              select
              label="Режим сбора"
              value={form.reading_collection_mode}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, reading_collection_mode: e.target.value }))
              }
            >
              {COLLECTION_MODES.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>
                  {mode.label}
                </MenuItem>
              ))}
            </TextField>

            {form.reading_collection_mode === 'automatic' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="DevEUI"
                  value={form.deveui}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, deveui: e.target.value.toUpperCase() }))
                  }
                  fullWidth
                />
                <TextField
                  label="MQTT topic"
                  value={form.mqttTopic}
                  onChange={(e) => setForm((prev) => ({ ...prev, mqttTopic: e.target.value }))}
                  fullWidth
                />
              </Stack>
            )}

            {isUrt && (
              <>
                <Divider />
                <Stack spacing={1.5}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Радиатор
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setManualHeatMode((prev) => !prev);
                          setSelectedRadiator(null);
                          setForm((prev) => ({
                            ...prev,
                            heat_coefficient: '',
                          }));
                        }}
                      >
                        {manualHeatMode ? 'Использовать каталог' : 'Ручной ввод'}
                      </Button>
                    </Stack>

                    {!manualHeatMode ? (
                      <Autocomplete
                        options={radiatorCatalogItems}
                        loading={radiatorCatalogLoading}
                        value={selectedRadiator}
                        onChange={handleRadiatorSelect}
                        inputValue={radiatorQuery}
                        onInputChange={(_, value) => setRadiatorQuery(value)}
                        getOptionLabel={formatRadiatorLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <FieldLabel
                                label="Радиатор из каталога"
                                title="Выберите установленный радиатор. Мощность и расчётный коэффициент берутся из справочника."
                              />
                            }
                            placeholder="Начните вводить название или модель"
                            helperText="Коэффициенты подставятся автоматически"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {option.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[
                                  option.model_code,
                                  option.manufacturer,
                                  option.power_watts != null ? `${option.power_watts} Вт` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' • ')}
                              </Typography>
                            </Stack>
                          </li>
                        )}
                      />
                    ) : (
                      <Stack spacing={1.5}>
                        <Alert severity="info" variant="outlined">
                          Ручной ввод нужен только если радиатора нет в каталоге.
                        </Alert>
                        <TextField
                          label={
                            <FieldLabel
                              label="Расчётный коэффициент радиатора"
                              title="Коэффициент конкретного радиатора. Обычно берётся из справочника на основе модели, мощности и размера радиатора."
                            />
                          }
                          type="number"
                          value={form.heat_coefficient}
                          onChange={(e) => setForm((prev) => ({ ...prev, heat_coefficient: e.target.value }))}
                          fullWidth
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            label="Модель радиатора"
                            value={form.radiator_model_name}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_model_name: e.target.value }))}
                            fullWidth
                          />
                          <TextField
                            label="Производитель"
                            value={form.radiator_manufacturer}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_manufacturer: e.target.value }))}
                            fullWidth
                          />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            label="Расположение"
                            value={form.radiator_placement}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_placement: e.target.value }))}
                            fullWidth
                          />
                          <TextField
                            label="Помещение / метка"
                            value={form.radiator_room_label}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_room_label: e.target.value }))}
                            fullWidth
                          />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            label="Количество секций"
                            type="number"
                            value={form.radiator_section_count}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_section_count: e.target.value }))}
                            fullWidth
                          />
                          <TextField
                            label={
                              <FieldLabel
                                label="Мощность, Вт"
                                title="Тепловая мощность радиатора. Помогает проверить или подобрать расчётный коэффициент."
                              />
                            }
                            type="number"
                            value={form.radiator_power_watts}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_power_watts: e.target.value }))}
                            fullWidth
                          />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            label="Высота, мм"
                            type="number"
                            value={form.radiator_height_mm}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_height_mm: e.target.value }))}
                            fullWidth
                          />
                          <TextField
                            label="Ширина, мм"
                            type="number"
                            value={form.radiator_width_mm}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_width_mm: e.target.value }))}
                            fullWidth
                          />
                          <TextField
                            label="Глубина, мм"
                            type="number"
                            value={form.radiator_depth_mm}
                            onChange={(e) => setForm((prev) => ({ ...prev, radiator_depth_mm: e.target.value }))}
                            fullWidth
                          />
                        </Stack>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.radiator_is_corner}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, radiator_is_corner: e.target.checked }))
                              }
                            />
                          }
                          label="Угловой радиатор"
                        />
                        <TextField
                          label="Примечание по радиатору"
                          value={form.radiator_notes}
                          onChange={(e) => setForm((prev) => ({ ...prev, radiator_notes: e.target.value }))}
                          multiline
                          minRows={2}
                        />
                      </Stack>
                    )}
                  </Stack>

                  {renderHeatSummary()}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label={
                        <FieldLabel
                          label="Дата установки"
                          title="Используется в расчётах по периодам. Если замен не было, поле можно оставить пустым."
                        />
                      }
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={form.heat_installation_date}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, heat_installation_date: e.target.value }))
                      }
                      fullWidth
                    />
                    <TextField
                      label={
                        <FieldLabel
                          label="Статус пломбы"
                          title="Служебный статус для контроля эксплуатации прибора."
                        />
                      }
                      value={form.heat_seal_status}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, heat_seal_status: e.target.value }))
                      }
                      fullWidth
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label={
                        <FieldLabel
                          label="Рабочий статус"
                          title="Отражает состояние прибора: установлен, работает, требует проверки или снят."
                        />
                      }
                      value={form.heat_operational_status}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, heat_operational_status: e.target.value }))
                      }
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.heat_is_direct}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, heat_is_direct: e.target.checked }))
                          }
                        />
                      }
                      label="Прямой прибор отопления"
                    />
                  </Stack>

                  <TextField
                    label="Примечание"
                    value={form.heat_notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, heat_notes: e.target.value }))}
                    multiline
                    minRows={2}
                  />
                </Stack>
              </>
            )}

            <TextField
              label="Комментарий"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={pendingCreateDevice || pendingCreateApartment}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit || pendingCreateDevice || pendingCreateApartment}
          >
            {pendingCreateApartment ? 'Создаём квартиру...' : pendingCreateDevice ? 'Сохраняем...' : 'Создать'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
